import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type {
  EntityListFilterConfig,
  EntityListSelectFilterConfig,
  EntityListTextFilterConfig,
} from '@/types/Entity';

type EntityListFiltersProps = {
  filters: EntityListFilterConfig[];
  values: Record<string, string | undefined>;
  onChange: (filter: EntityListFilterConfig, value?: string) => void;
  onClear?: () => void;
  resetVersion?: number;
};

const CLEAR_OPTION_VALUE = '__entity_list_filters_clear__';

const SelectFilterField: React.FC<{
  filter: EntityListSelectFilterConfig;
  value?: string;
  onChange: (value?: string) => void;
}> = ({ filter, value, onChange }) => {
  const hasClearOption = filter.clearable !== false;
  const selectValue =
    value ?? (hasClearOption ? CLEAR_OPTION_VALUE : (filter.options[0]?.value ?? ''));

  const handleChange = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value;
    if (hasClearOption && nextValue === CLEAR_OPTION_VALUE) {
      onChange(undefined);
      return;
    }
    onChange(nextValue);
  };

  return (
    <FormControl sx={{ minWidth: 200 }} size="small">
      <InputLabel id={`${filter.key}-label`}>{filter.label}</InputLabel>
      <Select
        labelId={`${filter.key}-label`}
        label={filter.label}
        value={selectValue}
        onChange={handleChange}
      >
        {filter.clearable !== false && (
          <MenuItem value={CLEAR_OPTION_VALUE}>
            <em>{filter.clearLabel ?? 'All'}</em>
          </MenuItem>
        )}
        {filter.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const TextFilterField: React.FC<{
  filter: EntityListTextFilterConfig;
  value?: string;
  onChange: (value?: string) => void;
  resetToken?: number;
}> = ({ filter, value, onChange, resetToken }) => {
  const [inputValue, setInputValue] = useState(value ?? '');
  const debounceMs = filter.debounceMs ?? 400;
  const debouncedValue = useDebouncedValue(inputValue, debounceMs);
  const valueRef = useRef(value);

  useEffect(() => {
    if (resetToken) {
      setInputValue('');
    }
  }, [resetToken]);

  useEffect(() => {
    valueRef.current = value;
    setInputValue(value ?? '');
  }, [value]);

  useEffect(() => {
    if (inputValue !== debouncedValue) {
      return;
    }
    const normalized = debouncedValue.trim();
    const next = normalized.length > 0 ? normalized : undefined;
    if (next !== valueRef.current) {
      onChange(next);
    }
  }, [debouncedValue, inputValue, onChange]);

  return (
    <TextField
      label={filter.label}
      placeholder={filter.placeholder}
      size="small"
      value={inputValue}
      onChange={(event) => setInputValue(event.target.value)}
      fullWidth
      sx={{ flexGrow: 1 }}
    />
  );
};

export const EntityListFilters: React.FC<EntityListFiltersProps> = ({
  filters,
  values,
  onChange,
  onClear,
  resetVersion,
}) => {
  if (!filters.length) {
    return null;
  }

  const hasActiveFilters = Object.values(values).some(
    (val) => val !== undefined && val !== '' && val !== CLEAR_OPTION_VALUE,
  );

  return (
    <Card sx={{ mb: 1 }}>
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        >
          {filters.map((filter) => {
            if (filter.type === 'select') {
              return (
                <SelectFilterField
                  key={filter.key}
                  filter={filter}
                  value={values[filter.key]}
                  onChange={(nextValue) => onChange(filter, nextValue)}
                />
              );
            }
            return (
              <TextFilterField
                key={filter.key}
                filter={filter}
                value={values[filter.key]}
                onChange={(nextValue) => onChange(filter, nextValue)}
                resetToken={resetVersion}
              />
            );
          })}

          {onClear && (
            <Button
              variant="outlined"
              onClick={onClear}
              disabled={!hasActiveFilters}
              sx={{ whiteSpace: 'nowrap', padding: '6px 18px', minWidth: 150 }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
